/**
 * Icon components using @ant-design/icons (already provided by antd).
 * Wraps icons with consistent size/strokeWidth API for easier migration.
 */
import React from "react";
import {
  SearchOutlined,
  TeamOutlined,
  BarChartOutlined,
  MessageOutlined,
  DashboardOutlined,
  FileTextOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  EnvironmentOutlined,
  RocketOutlined,
  BulbOutlined,
  ReadOutlined,
  ThunderboltOutlined,
  CalculatorOutlined,
  ExperimentOutlined,
  RobotOutlined,
  UnorderedListOutlined,
  AimOutlined,
  CalendarOutlined,
  QuestionCircleOutlined,
  InfoCircleOutlined,
  TrophyOutlined,
  StarOutlined,
  UserOutlined,
  CheckOutlined,
  CloseOutlined,
  LoadingOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  LockOutlined,
  ChromeOutlined,
  MailOutlined,
  CloseCircleOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  BellOutlined,
  PhoneOutlined,
  MobileOutlined,
  LinkOutlined,
  ReloadOutlined,
  StopOutlined,
  KeyOutlined,
  BookOutlined,
  CrownOutlined,
  MessageOutlined as MessageSquareIcon,
  BgColorsOutlined,
  SettingOutlined,
  AppstoreOutlined,
  MinusCircleFilled,
  UpOutlined,
  DownOutlined,
  UsergroupAddOutlined,
  ClockCircleOutlined,
  HeartOutlined,
  LeftOutlined,
  RightOutlined,
  UploadOutlined,
  DownloadOutlined,
  PaperClipOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";

type IconProps = {
  size?: number;
  strokeWidth?: number;
  className?: string;
  style?: React.CSSProperties;
  spin?: boolean;
};

const iconProps = (props: IconProps) => ({
  className: props.className,
  style: { fontSize: props.size ?? 24, ...props.style },
  spin: props.spin,
});

export const Search = (p: IconProps) => <SearchOutlined {...iconProps(p)} />;
export const Users = (p: IconProps) => <TeamOutlined {...iconProps(p)} />;
export const BarChart3 = (p: IconProps) => (
  <BarChartOutlined {...iconProps(p)} />
);
export const MessageCircle = (p: IconProps) => (
  <MessageOutlined {...iconProps(p)} />
);
export const LayoutDashboard = (p: IconProps) => (
  <DashboardOutlined {...iconProps(p)} />
);
export const FileText = (p: IconProps) => (
  <FileTextOutlined {...iconProps(p)} />
);
export const AlertTriangle = (p: IconProps) => (
  <WarningOutlined {...iconProps(p)} />
);
export const CheckCircle = (p: IconProps) => (
  <CheckCircleOutlined {...iconProps(p)} />
);
export const MapPin = (p: IconProps) => (
  <EnvironmentOutlined {...iconProps(p)} />
);
export const Rocket = (p: IconProps) => <RocketOutlined {...iconProps(p)} />;
export const Lightbulb = (p: IconProps) => <BulbOutlined {...iconProps(p)} />;
export const Brain = (p: IconProps) => <BulbOutlined {...iconProps(p)} />;
export const Zap = (p: IconProps) => <ThunderboltOutlined {...iconProps(p)} />;
export const Calculator = (p: IconProps) => (
  <CalculatorOutlined {...iconProps(p)} />
);
export const FlaskConical = (p: IconProps) => (
  <ExperimentOutlined {...iconProps(p)} />
);
export const Bot = (p: IconProps) => <RobotOutlined {...iconProps(p)} />;
export const ClipboardList = (p: IconProps) => (
  <UnorderedListOutlined {...iconProps(p)} />
);
export const GraduationCap = (p: IconProps) => (
  <ReadOutlined {...iconProps(p)} />
);
export const BookOpen = (p: IconProps) => <BookOutlined {...iconProps(p)} />;
export const Target = (p: IconProps) => <AimOutlined {...iconProps(p)} />;
export const Calendar = (p: IconProps) => (
  <CalendarOutlined {...iconProps(p)} />
);
export const HelpCircle = (p: IconProps) => (
  <QuestionCircleOutlined {...iconProps(p)} />
);
export const Info = (p: IconProps) => <InfoCircleOutlined {...iconProps(p)} />;
export const Medal = (p: IconProps) => <TrophyOutlined {...iconProps(p)} />;
export const Star = (p: IconProps) => <StarOutlined {...iconProps(p)} />;
export const UserCircle = (p: IconProps) => <UserOutlined {...iconProps(p)} />;
export const Check = (p: IconProps) => <CheckOutlined {...iconProps(p)} />;
export const X = (p: IconProps) => <CloseOutlined {...iconProps(p)} />;
export const Loader2 = (p: IconProps) => (
  <LoadingOutlined {...iconProps(p)} spin />
);
export const Eye = (p: IconProps) => <EyeOutlined {...iconProps(p)} />;
export const EyeOff = (p: IconProps) => (
  <EyeInvisibleOutlined {...iconProps(p)} />
);
export const Lock = (p: IconProps) => <LockOutlined {...iconProps(p)} />;
export const Chrome = (p: IconProps) => <ChromeOutlined {...iconProps(p)} />;
export const Mail = (p: IconProps) => <MailOutlined {...iconProps(p)} />;
export const XCircle = (p: IconProps) => (
  <CloseCircleOutlined {...iconProps(p)} />
);
export const Plus = (p: IconProps) => <PlusOutlined {...iconProps(p)} />;
export const Pencil = (p: IconProps) => <EditOutlined {...iconProps(p)} />;
export const Trash2 = (p: IconProps) => <DeleteOutlined {...iconProps(p)} />;
export const Bell = (p: IconProps) => <BellOutlined {...iconProps(p)} />;
export const Phone = (p: IconProps) => <PhoneOutlined {...iconProps(p)} />;
export const Smartphone = (p: IconProps) => (
  <MobileOutlined {...iconProps(p)} />
);
export const Link2 = (p: IconProps) => <LinkOutlined {...iconProps(p)} />;
export const RefreshCw = (p: IconProps) => <ReloadOutlined {...iconProps(p)} />;
export const Ban = (p: IconProps) => <StopOutlined {...iconProps(p)} />;
export const Key = (p: IconProps) => <KeyOutlined {...iconProps(p)} />;
export const Crown = (p: IconProps) => <CrownOutlined {...iconProps(p)} />;
export const MessageSquare = (p: IconProps) => (
  <MessageSquareIcon {...iconProps(p)} />
);
export const Palette = (p: IconProps) => <BgColorsOutlined {...iconProps(p)} />;
export const Settings = (p: IconProps) => <SettingOutlined {...iconProps(p)} />;
export const Puzzle = (p: IconProps) => <AppstoreOutlined {...iconProps(p)} />;
export const Circle = (p: IconProps) => <MinusCircleFilled {...iconProps(p)} />;
export const ChevronUp = (p: IconProps) => <UpOutlined {...iconProps(p)} />;
export const ChevronDown = (p: IconProps) => <DownOutlined {...iconProps(p)} />;
export const ChevronRight = (p: IconProps) => (
  <RightOutlined {...iconProps(p)} />
);
export const Sparkles = (p: IconProps) => <StarOutlined {...iconProps(p)} />;
export const Handshake = (p: IconProps) => (
  <UsergroupAddOutlined {...iconProps(p)} />
);
export const Trophy = (p: IconProps) => <TrophyOutlined {...iconProps(p)} />;
export const Sprout = (p: IconProps) => <BulbOutlined {...iconProps(p)} />;
export const Dumbbell = (p: IconProps) => <TrophyOutlined {...iconProps(p)} />;
export const Bookmark = (p: IconProps) => <BookOutlined {...iconProps(p)} />;
export const Clock = (p: IconProps) => (
  <ClockCircleOutlined {...iconProps(p)} />
);
export const Heart = (p: IconProps) => <HeartOutlined {...iconProps(p)} />;
export const ArrowLeft = (p: IconProps) => <LeftOutlined {...iconProps(p)} />;
export const Upload = (p: IconProps) => <UploadOutlined {...iconProps(p)} />;
export const Download = (p: IconProps) => (
  <DownloadOutlined {...iconProps(p)} />
);
export const Paperclip = (p: IconProps) => (
  <PaperClipOutlined {...iconProps(p)} />
);
export const PlayCircle = (p: IconProps) => (
  <PlayCircleOutlined {...iconProps(p)} />
);
